<template>
  <div class="box container config-box">
    <div class="columns is-vcentered mb-2">
      <div class="column is-6">
        <h1 class="title is-4 mb-0">
          <span class="icon has-text-info"><i class="fa fa-cogs"></i></span>
          {{ $t("configuration file") }}
        </h1>
      </div>
      <div class="column is-6 has-text-right">
        <div class="buttons">
          <button
            class="button is-primary is-rounded"
            @click="save"
            v-if="meta"
          >
            <span class="icon"><i class="fa fa-save"></i></span>
            <span>{{ $t("save") }}</span>
          </button>
          <button
            class="button is-primary is-rounded"
            @click="init"
            v-if="!meta"
          >
            <span class="icon"><i class="fa fa-edit"></i></span>
            <span>{{ $t("edit") }}</span>
          </button>
          <button class="button is-danger is-rounded" @click="reset">
            <span class="icon"><i class="fa fa-refresh"></i></span>
            <span>{{ $t("reset") }}</span>
          </button>
        </div>
      </div>
    </div>

    <form v-if="meta">
      <!-- Instance Section -->
      <section class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-server"></i></span>
          <span>{{ $t("instance") }}</span>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("default enterprise") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.default_enterprise"
                />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("instance url") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.instance_url" />
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("instance name") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.instance_name" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("logo url") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.logo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Language & Enterprise Field -->
      <section class="section-block">
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("default language") }}</label>
              <div class="control">
                <div class="select">
                  <select v-model="meta.default_language">
                    <option value="es">{{ $t("spanish") }}</option>
                    <option value="en">{{ $t("english") }}</option>
                    <option value="fr">{{ $t("french") }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field mt-5">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_hide_enterprise"
                v-model="meta.hide_enterprise_field"
              />
              <label for="chk_hide_enterprise">{{
                $t("hide enterprise field on login")
              }}</label>
            </div>
          </div>
        </div>
      </section>

      <!-- Python API Section -->
      <section class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-python"></i></span>
          <span>{{ $t("modul python api") }}</span>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field mt-5">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_start_python_api_on_startup"
                v-model="meta.start_python_api_on_startup"
              />
              <label for="chk_start_python_api_on_startup">{{
                $t("start python api server on startup")
              }}</label>
            </div>
          </div>
          <div class="column">
            <label class="label">{{ $t("python binary path") }}</label>
            <div class="field">
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.python_binary_path"
                  placeholder="/usr/bin/python"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <label class="label">{{ $t("modul api python url") }}</label>
            <div class="field has-addons">
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.python_api_url"
                  placeholder="http://localhost:5000"
                />
              </div>
              <div class="control">
                <button
                  class="button is-info is-rounded"
                  :data-tooltip="$t('test')"
                  @click="test_python_api_url"
                >
                  <span class="icon"><i class="fa fa-bolt"></i></span>
                </button>
              </div>
            </div>
          </div>
          <div class="column">
            <label class="label">{{
              $t("local api python server port")
            }}</label>
            <div class="field has-addons">
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.python_api_port"
                  placeholder="5000"
                />
              </div>
              <div class="control">
                <button
                  class="button is-info is-rounded"
                  :data-tooltip="$t('test')"
                  @click="test_local_python_api"
                >
                  <span class="icon"><i class="fa fa-bolt"></i></span>
                </button>
              </div>
              <div class="control">
                <button
                  class="button is-danger is-rounded"
                  :data-tooltip="$t('stop')"
                  @click="stop_local_python_api"
                >
                  <span class="icon"><i class="fa fa-stop"></i></span>
                </button>
              </div>
              <div class="control">
                <button
                  class="button is-primary is-rounded"
                  :data-tooltip="$t('start server')"
                  @click="start_local_python_api"
                >
                  <span class="icon"><i class="fa fa-play"></i></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Modul Repository Section -->
      <section class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-database"></i></span>
          <span>{{ $t("modul repository") }}</span>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("modul repository host") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.modul_repository_host"
                />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("modul repository user") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.modul_repository_user"
                />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("modul repository password") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="password"
                  v-model="meta.modul_repository_password"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Github Section -->
      <section class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-github"></i></span>
          <span>{{ $t("github repository") }}</span>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("github repository url") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.git_url" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("git user") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.git_user" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("git password") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="password"
                  v-model="meta.git_password"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- System Variables -->
      <section class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-cog"></i></span>
          <span>{{ $t("system variables") }}</span>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field mt-5">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_variable_NODE_TLS_REJECT_UNAUTHORIZED"
                v-model="meta.NODE_TLS_REJECT_UNAUTHORIZED"
              />
              <label for="chk_variable_NODE_TLS_REJECT_UNAUTHORIZED">{{
                $t("NODE_TLS_REJECT_UNAUTHORIZED")
              }}</label>
            </div>
          </div>
        </div>
      </section>

      <!-- Front-end Section -->
      <section class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-desktop"></i></span>
          <span>{{ $t("front-end") }}</span>
        </div>
        <article class="message is-warning mb-3">
          <div class="message-body">
            {{
              $t(
                "to apply this settings you must build after save the new settings"
              )
            }}
          </div>
        </article>
        <div class="columns">
          <div class="column">
            <div class="field mt-5">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_validate_permissions_in_frontent"
                v-model="meta.validate_permissions_in_frontend"
              />
              <label for="chk_validate_permissions_in_frontent">{{
                $t("validate permissions in front-end")
              }}</label>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("timeout") }}</label>
              <div class="control">
                <input class="input" type="number" v-model="meta.timeout" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("language") }}</label>
              <div class="control">
                <div class="select">
                  <select v-model="meta.language">
                    <option value="es">{{ $t("spanish") }}</option>
                    <option value="en">{{ $t("english") }}</option>
                    <option value="fr">{{ $t("french") }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("api url") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.api_host" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("websocket url") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.web_socket_host"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Database Section -->
      <section v-if="meta.database" class="section-block">
        <div class="section-title">
          <span class="icon has-text-info"><i class="fa fa-database"></i></span>
          <span>{{ $t("database") }}</span>
        </div>
        <article class="message is-warning mb-3">
          <div class="message-body">
            {{ $t("to apply this settings you must restart modul service") }}
          </div>
        </article>
        <div class="columns">
          <div class="column">
            <div class="field mt-5">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_database_insecure_auth"
                v-model="meta.database.insecureAuth"
              />
              <label for="chk_database_insecure_auth">{{
                $t("insecure auth")
              }}</label>
            </div>
            <div class="field mt-2">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_database_logging"
                v-model="meta.database.logging"
              />
              <label for="chk_database_logging">{{ $t("logging") }}</label>
            </div>
          </div>
          <div class="column">
            <div class="field mt-5">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_database_minify_aliases"
                v-model="meta.database.minifyAliases"
              />
              <label for="chk_database_minify_aliases">{{
                $t("minify aliases")
              }}</label>
            </div>
            <div class="field mt-2">
              <input
                class="is-checkradio"
                type="checkbox"
                id="chk_database_benchmark"
                v-model="meta.database.benchmark"
              />
              <label for="chk_database_benchmark">{{ $t("benchmark") }}</label>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("dialect") }}</label>
              <div class="control">
                <div class="select">
                  <select v-model="meta.database.dialect">
                    <option value="mysql">MySQL</option>
                    <option value="mariadb">MariaDB</option>
                    <option value="postgres">PostgreSQL</option>
                    <option value="sqlite">SQLite</option>
                    <option value="mssql">MS SQL</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("host") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="meta.database.host" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("port") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="number"
                  v-model="meta.database.port"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("database") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.database.database"
                />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("username") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.database.username"
                />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("password") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="password"
                  v-model="meta.database.password"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </form>
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>
  </div>
</template>

<script>
// import EnterpriseModal from "./enterprise_modal/Index.vue";

import {
  get,
  post,
  success_message,
  error_message,
  confirm_deletion_dialog,
  generate_error_message_text,
} from "@/utils";

export default {
  name: "SettingsConfig",
  // components: { EnterpriseModal },
  data() {
    return {
      meta: undefined,
      error: "",
    };
  },
  methods: {
    // refresh() {
    //   let v = this;
    //   v.datatables_instance.ajax.reload();
    // },
    init() {
      let v = this;
      v.$root.loader(true);
      get("/core/file", {})
        .then((response) => {
          v.meta = response;
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    save() {
      let v = this;
      v.$root.loader(true);
      post("/core/file", v.meta)
        .then((/* response */) => {
          // v.meta = response;
          success_message(v.$t("file saved"));
          v.error = "";
          v.meta = undefined;
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    reset() {
      let v = this;
      confirm_deletion_dialog(
        v.$t("are you sure to restart system?"),
        v,
        () => {
          get("/core/restart", {})
            .then(() => {
              success_message(v.$t("system restarted"));
            })
            .catch((error) => {
              v.error = generate_error_message_text(error);
            });
        }
      );
    },
    test_python_api_url() {
      let v = this;
      v.$root.loader(true);
      post("/core/test-apy-url", { url: v.meta.python_api_url })
        .then((response) => {
          success_message(v.$t("test successful"));
        })
        .catch((error) => {
          console.error(error);
          error_message(v.$t("test failed"));
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    test_local_python_api() {
      let v = this;
      v.$root.loader(true);
      post("/core/test-local-apy", {
        port: v.meta.python_api_port,
      })
        .then((response) => {
          success_message(v.$t("test successful"));
        })
        .catch((error) => {
          console.error(error);
          error_message(v.$t("test failed"));
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    start_local_python_api() {
      let v = this;
      v.$root.loader(true);
      post("/core/start-local-apy", {})
        .then((response) => {
          success_message(v.$t("server started"));
        })
        .catch((error) => {
          console.error(error);
          error_message(generate_error_message_text(error));
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    stop_local_python_api() {
      let v = this;
      v.$root.loader(true);
      post("/core/stop-local-apy", {})
        .then((response) => {
          success_message(v.$t(response));
        })
        .catch((error) => {
          console.error(error);
          error_message(generate_error_message_text(error));
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
  },
  mounted() {
    // let v = this;
    // v.init();
  },
};
</script>

<style scoped>
.config-box {
  max-width: 1100px;
  margin: 2rem auto;
  padding: 2rem 2rem 1.5rem 2rem;
  border-radius: 18px;
  box-shadow: 0 2px 16px rgba(20, 22, 26, 0.45);
  /* background: #23272f; */
  color: #e6e6e6;
}
.section-block {
  margin-bottom: 2.2rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid #353b48;
}
.section-block:last-child {
  border-bottom: none;
}
.section-title {
  font-size: 1.18em;
  font-weight: 600;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5em;
  color: #4fc3f7;
}
input.input,
select.select {
  border-radius: 8px;
  font-size: 1em;
  background: #181a20;
  color: #e6e6e6;
  border: 1.5px solid #353b48;
}
input.input:focus,
select.select:focus {
  border-color: #4fc3f7;
  box-shadow: 0 0 0 1.5px #4fc3f7;
  background: #23272f;
  color: #fff;
}
.field label.label {
  font-weight: 500;
  color: #b2becd;
}
.buttons .button {
  margin-left: 0.5em;
}
.buttons .button:first-child {
  margin-left: 0;
}
.button,
.button.is-primary,
.button.is-danger,
.button.is-info {
  background: #353b48;
  color: #e6e6e6;
  border: none;
}
.button.is-primary {
  background: #0984e3;
  color: #fff;
}
.button.is-danger {
  background: #d63031;
  color: #fff;
}
.button.is-info {
  background: #00b8d9;
  color: #fff;
}
.button.is-primary:focus,
.button.is-danger:focus,
.button.is-info:focus,
.button:focus {
  outline: 2px solid #4fc3f7;
}
.message.is-warning {
  background: #2d2d1f;
  border-left: 4px solid #ffbb33;
  color: #ffeaa7;
}
.notification.is-danger {
  background: #2d2323;
  color: #ff7675;
  border-left: 4px solid #d63031;
}
::-webkit-input-placeholder {
  color: #888 !important;
}
::-moz-placeholder {
  color: #888 !important;
}
:-ms-input-placeholder {
  color: #888 !important;
}
::placeholder {
  color: #888 !important;
}
</style>
